import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tab, Button, Icon, Modal, COLORS } from "abs-ui-components";
import { Tooltip, Zoom } from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import axiosHttp from "../../services/interceptor";
import "./UKETSAerReport.scss";
import Loader from "../../common/Loader/Loader";
import { emitToasterMsg } from "../../services/toasterUtility";
import { ClaimsContext } from "../../services/ClaimsService";
import { ClaimType } from "../../constants/claims";
import { useSelector } from "react-redux";
import { getUKETSAERDetails } from "../../services/UKETSAerService";
import UKETSCompanyDetails from "./CompanyDetails/UKETSCompanyDetails";
import UKETSListOfShips from "./ListOfShips/UKETSListOfShips";
import UKETSReportSummary from "./ReportSummary/UKETSReportSummary";
import UKETSDocuments from "./UKETSDocuments/UKETSDocuments";
import UKETSVerification from "./UKETSVerification/UKETSVerification";
import {deleteUKETSAER } from "../../services/UKETSAerService";
import moment from "moment";
const getStatusIcon = (status) => {
	switch (status) {
		case "Verification Completed":
			return "check";
		case "No Data Submitted":
			return "info";
		case "Verification In Progress":
			return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
		case "Crosscheck In Progress":
			return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
		case "Supporting Documents Needed":
			return <InsertDriveFileIcon sx={{ fontSize: 16 }} />;
		case "Under Revision":
			return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
		default:
			return null;
	}
};

const UKETSAerReport = ({ onTabChange }) => {
	const { ukEtsAerMstId } = useParams();
	const { userHasClaim } = useContext(ClaimsContext);
	const user = useSelector((state) => state.userInfo.userData);
	const [isExporting, setIsExporting] = useState(false);
	const [aerDetails, setAerDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedTab, setSelectedTab] = useState(0);
    const [isEditable, setIsEditable] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
	
	useEffect(() => {
		if (ukEtsAerMstId) {
			loadDetails();
		}
	}, [ukEtsAerMstId]);

	const loadDetails = async () => {
		setLoading(true);
		try {
			const data = await getUKETSAERDetails(ukEtsAerMstId);
			setAerDetails(data);
		} catch (err) {
			emitToasterMsg("error", "Error", "Failed to load report details.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (aerDetails) {
					 const isEditable = aerDetails?.status === "No Data Submitted" || aerDetails.status === "Under Revision";
					 setIsEditable(isEditable);
		}
	}, [aerDetails]);

	const handleReturnToReportList = () => {
		navigate(`/uk-ets-annual-emission-report`);
	};

	const handleTabChange = (index) => {
		setSelectedTab(index);
		const labels = ["Company Details", "List of Ships", "Report Summary", "Documents", "Verification"];
		onTabChange && onTabChange(labels[index] || "");
	};
	const navigate = useNavigate();

	const handleExport = async () => {
		if (isExporting) {
			return;
		}

		setIsExporting(true);

		try {
			const reportIdentifier = aerDetails?.ukEtsAerId || "UKETS_AER_Report";
			const sanitizedReportIdentifier = String(reportIdentifier)
				.trim()
				.replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
				.replace(/\s+/g, "-");
			let defaultFileName = `${sanitizedReportIdentifier}.xlsx`;
			var exportUrl = `api/UkEtsCompanyReport/ExportUkEtsCompanyReportById?ukEtsCompanyLevelReportId=${ukEtsAerMstId}`;
			const res = await axiosHttp({
				url: exportUrl,
				method: "GET",
				responseType: "blob",
			});

			const contentDisposition = res.headers["content-disposition"];
			let fileName = defaultFileName;
			if (contentDisposition) {
				const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
				if (fileNameMatch && fileNameMatch[1]) {
					fileName = fileNameMatch[1].replace(/"/g, "");
				}
			}

			const blob = new Blob([res.data], {
				type: res.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = url;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			emitToasterMsg("success", "Export", "File downloaded successfully.");
		} catch (error) {
			emitToasterMsg("error", "Export Failed", error?.response?.data?.message || "An unexpected error occurred during the export process.");
			console.error("Export Error:", error);
		} finally {
			setIsExporting(false);
		}
	};

	const getDeleteState = ({ reviewerRole, verifierRole, superAdminRole, status }) => {
		const isVerificationCompleted = status === "Verification Completed";

		if (superAdminRole) {
			return {
				show: true,
				disabled: false,
			};
		}
		if (isVerificationCompleted) {
			return {
				show: false,
			};
		}
		if (!(reviewerRole || verifierRole || superAdminRole) && status !== "No Data Submitted") {
			return {
				show: false,
			};
		}
		return {
			show: true,
			disabled: isVerificationCompleted,
		};
	};
    
	const canDeleteAER = () => {
		if (!userHasClaim(ClaimType.DELETE_REPORT)) {
			return { show: false, disabled: true };
		}

		const reviewerRole = user?.roles?.includes("ER_REVIEWER");
		const verifierRole = user?.roles?.includes("ER_VERIFIER");
		const superAdminRole = user?.roles?.includes("ER_SUPERADMIN");

		return getDeleteState({
			reviewerRole,
			verifierRole,
			superAdminRole,
			status: aerDetails?.status,
		});
	};

    const { show: showDeleteButton, disabled: isDeleteDisabled } = canDeleteAER();
	const handleDelete = async () => {
		try {
			setIsDeleting(true);
			 await deleteUKETSAER(ukEtsAerMstId);
			emitToasterMsg("success", "UKETS AER Report", "Deleted Successfully");
			setConfirmDelete(false);
			navigate(`/uk-ets-annual-emission-report`, { replace: true });
		} catch (error) {
			console.error("Error deleting UKETS AER Report:", error);
			emitToasterMsg("error", "UKETS AER Report", "Failed to delete the report");
		} finally {
			setIsDeleting(false);
		}
	};

	const tabData = [
		{
			header: "Company Details",
			content:
				selectedTab === 0 ? (
					<UKETSCompanyDetails ukEtsAerMstId={ukEtsAerMstId} aerDetails={aerDetails} isEditable={isEditable} onDetailsUpdated={loadDetails} />
				) : null,
		},
		{
			header: "List of Ships",
			content:
				selectedTab === 1 ? (
					<UKETSListOfShips
						ukEtsAerMstId={ukEtsAerMstId}
						isEditable={isEditable}
						reportStatus={aerDetails?.reportStatus}
						status={aerDetails?.status}
					/>
				) : null,
		},
		{
			header: "Report Summary",
			content:
				selectedTab === 2 ? (
					<UKETSReportSummary ukEtsAerMstId={ukEtsAerMstId} aerDetails={aerDetails} loadDetails={loadDetails} status={aerDetails?.status} />
				) : null,
		},
		{
			header: "Documents",
			content: selectedTab === 3 ? <UKETSDocuments ukEtsAerMstId={ukEtsAerMstId} isEditable={isEditable} /> : null,
		},
		...(userHasClaim(ClaimType.VIEW_VERIFICATION_TAB)
			? [
					{
						header: "Verification",
						content: selectedTab === 4 ? <UKETSVerification ukEtsCompanyReportId={ukEtsAerMstId}  aerDetails={aerDetails} loadDetails={loadDetails}/> : null,
					},
				]
			: []),
	];

	if (loading) return <Loader loaderName="Loading report..." />;
    
	const formatDate = (dateString) => {
			return moment(dateString).format("DD-MMM-YYYY");
		};

	const statusIcon = getStatusIcon(aerDetails?.status);
		const formattedDate = aerDetails?.verificationCompletedDate
		? formatDate(aerDetails?.verificationCompletedDate)
		: "";
	console.log("aerDetails", aerDetails);
    const tooltipTitle =
			aerDetails?.status === "Verification Completed" ? (
				<>
					Reviewer : <span className="font-style">{aerDetails?.reviewer}</span>
					<br />
					Verifier : <span className="font-style">{aerDetails?. verifier}</span>
					<br />
					Verification Complete Date : {formattedDate}
				</>
			) :(
				""
			);
	return (
		<div>
			<div className="mb-2 d-flex justify-content-between align-items-start mr-5">
				<div className="report-info">
					<div>
						<span style={{ fontWeight: 500, fontSize: "1.5rem" }}>{aerDetails?.ukEtsAerId || ""}</span>
						{aerDetails?.companyImoNumber ? (
							<span style={{ fontWeight: 500, color: "#6c757d", marginLeft: "24px" }}>
								{aerDetails?.companyName}, IMO# {aerDetails?.companyImoNumber}
							</span>
						) : (
							<span style={{ fontWeight: 500, color: "#6c757d", marginLeft: "24px" }}>{aerDetails?.companyName}</span>
						)}
					</div>
				</div>

				{/* Right side: status + action buttons */}
				<div className="d-flex align-items-center" style={{ gap: "8px", flexWrap: "wrap" }}>
					{/* Status badge — disabled button, same pattern as ReportDetails.js */}
					<Button variant="tertiary" startIcon={<Icon icon="arrow_back" />} className="mr-4" onClick={handleReturnToReportList}>
						Return to Report List
					</Button>

					<Button
						variant="tertiary"
						startIcon={isExporting ? <HourglassEmptyIcon sx={{ fontSize: 18 }} /> : <Icon icon="ios_share" />}
						className="mr-4"
						onClick={handleExport}
						disabled={isExporting}
					>
						{isExporting ? "Exporting..." : "Export Report"}
					</Button>

					{showDeleteButton && (
						<Button
							variant="tertiary"
							startIcon={<Icon icon="delete" />}
							className="mr-4"
							disabled={isDeleteDisabled}
							onClick={() => setConfirmDelete(true)}
						>
							Delete UKETS AER
						</Button>
					)}

					{aerDetails?.status && (
						<Tooltip
							title={tooltipTitle}
							disableHoverListener={!tooltipTitle}
							placement="top"
							TransitionComponent={Zoom}
							followCursor={true}
							arrow
							componentsProps={{
								tooltip: {
									sx: {
										fontSize: "13px",
										backgroundColor: "#012c51 !important",
										color: "white",
									},
								},
							}}
						>
							<span style={{ display: "inline-flex", alignItems: "center" }}>
								<Button
									variant="tertiary"
									startIcon={statusIcon && typeof statusIcon === "string" ? <Icon icon={statusIcon} /> : statusIcon}
									disabled
								>
									{aerDetails.status}
								</Button>
							</span>
						</Tooltip>
					)}
				</div>
			</div>

			<div className="uketsaer-report-details">
				<Tab
					tabData={tabData.map((tab) => ({
						...tab,
						header: <span style={{ textTransform: "capitalize" }}>{tab.header}</span>,
					}))}
					onTabChange={handleTabChange}
					activeTab={selectedTab}
				/>
			</div>

			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => handleDelete()}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this UKETS AER Report</div>
				</Modal>
			</div>
		</div>
	);
};

export default UKETSAerReport;
